// Kept free of imports so the Sentry plugin can recognise refresh failures without
// pulling in the token service (which itself depends on the Sentry plugin).

export type RefreshFailureReason =
  | 'no-refresh-token'
  | 'auth-rejected'
  | 'user-blocked'
  | 'transient';

export const REFRESH_FAILURE_REASONS: readonly RefreshFailureReason[] = [
  'no-refresh-token',
  'auth-rejected',
  'user-blocked',
  'transient',
];

export const REFRESH_ERROR_MESSAGES = {
  noRefreshToken: 'Could not find refreshToken',
  transient: 'JWT refresh failed transiently',
  rejected: 'JWT refresh rejected by the authentication service',
  userBlocked: 'JWT refresh rejected: user is blocked',
  throttled: 'JWT refresh throttled after a burst of refresh attempts',
} as const;

/** The subset that is sent to Sentry, and therefore must survive the ignore filters. */
export const REPORTED_REFRESH_ERROR_MESSAGES: readonly string[] = [
  REFRESH_ERROR_MESSAGES.rejected,
  REFRESH_ERROR_MESSAGES.throttled,
];

interface TokenRefreshErrorContext {
  reason: RefreshFailureReason;
  httpStatus?: number;
  authCode?: string;
  cause?: unknown;
}

export class TokenRefreshError extends Error {
  public readonly reason: RefreshFailureReason;

  public readonly httpStatus?: number;

  public readonly authCode?: string;

  public readonly cause?: unknown;

  constructor(message: string, { reason, httpStatus, authCode, cause }: TokenRefreshErrorContext) {
    super(message);
    this.name = 'TokenRefreshError';
    this.reason = reason;
    this.httpStatus = httpStatus;
    this.authCode = authCode;
    this.cause = cause;
  }
}

export const isTokenRefreshError = (error: unknown): error is TokenRefreshError =>
  error instanceof Error && error.name === 'TokenRefreshError';

export const isRefreshFailureReason = (value: unknown): value is RefreshFailureReason =>
  REFRESH_FAILURE_REASONS.includes(value as RefreshFailureReason);
